'use server';

import request from '../../../api/request';
import { actionKitImpFunction } from '../../../components/ozmoit/common/action-kit-imp';
import {
  PaginationQueryDto,
  PaginationRequestType,
} from '../../../types/paginationRequestType';
import {
  StudentRiskEvaluationSession,
  StudentRiskEvaluationSessionEnrollment,
  StudentRiskEvaluationSessionParams,
  StudentRiskEvaluationSessionsParams,
  StudentWarningsGroupType,
  StudentWarningsSummaryDataParams,
  StudentWarningsSummaryDetailsParams,
  StudentWarningsSummaryDetailsResponse,
  StudentWarningsSummaryFormData,
  StudentWarningsSummaryFormDetailsResponse,
  StudentWarningsSummaryRow,
} from '../types';

const getFormDataAction = actionKitImpFunction(async () => {
  const response = await request<StudentWarningsSummaryFormData>(
    'maintainers-portal/student-warnings/form/summary',
  );
  if (response.error) {
    throw new Error('Error fetching student warnings form data');
  }
  return response.data;
});

const getFormDetailsAction = actionKitImpFunction(async () => {
  const response = await request<StudentWarningsSummaryFormDetailsResponse>(
    'maintainers-portal/student-warnings/form/details',
  );
  if (response.error) {
    throw new Error('Error fetching student data');
  }
  return response.data;
});

const getEvaluationSessionsAction = actionKitImpFunction(
  async ({
    page,
    ...params
  }: PaginationQueryDto<StudentRiskEvaluationSessionsParams>): Promise<
    PaginationRequestType<StudentRiskEvaluationSession>
  > => {
    const response = await request<
      PaginationRequestType<StudentRiskEvaluationSession>
    >('maintainers-portal/student-risk-evaluation-session', {
      params: {
        ...params,
        page: Math.max(page - 1, 0),
      },
    });
    if (response.error) {
      throw new Error('Error fetching student warnings summary data');
    }
    return response.data;
  },
);

const getSummaryPaginatedDataAction = actionKitImpFunction(
  async ({
    page,
    ...params
  }: PaginationQueryDto<StudentWarningsSummaryDataParams>): Promise<
    PaginationRequestType<StudentWarningsSummaryRow>
  > => {
    const requiresParams = [
      !params?.schoolId,
      params?.warningGroupType === 'summary' &&
        (!params?.periodId || !params?.year),
      params?.warningGroupType === StudentWarningsGroupType.Academic &&
        !params?.periodId,
      params?.warningGroupType === StudentWarningsGroupType.Health &&
        !params?.year,
      params?.warningGroupType === StudentWarningsGroupType.Behavior &&
        !params?.year,
    ];
    if (requiresParams.some((requirement) => requirement)) {
      return {
        data: [],
        total: 0,
        total_pages: 0,
      };
    }

    const response = await request<
      PaginationRequestType<StudentWarningsSummaryRow>
    >('maintainers-portal/student-warnings/summary', {
      params: {
        ...params,
        page: Math.max(page - 1, 0),
      },
    });
    if (response.error) {
      throw new Error('Error fetching student warnings summary data');
    }
    return response.data;
  },
);

const getDetailsAction = actionKitImpFunction(
  async (params: StudentWarningsSummaryDetailsParams) => {
    const response = await request<StudentWarningsSummaryDetailsResponse>(
      `maintainers-portal/student-warnings/summary/details`,
      {
        params: {
          ...params,
          groupType: params.warningGroupType,
        },
      },
    );
    if (response.error) {
      throw new Error('Error fetching student data');
    }
    return response.data;
  },
);

const exportDetailsAction = actionKitImpFunction(
  async (params: StudentWarningsSummaryDetailsParams) => {
    const response = await request<BlobPart>(
      `maintainers-portal/student-warnings/summary/export`,
      {
        responseType: 'blob',
        params: {
          ...params,
          groupType: params.warningGroupType,
        },
      },
    );
    if (response.error) {
      throw new Error('Error fetching student data');
    }
    return response.data;
  },
);

const exportSummaryAction = actionKitImpFunction(
  async (
    params: Omit<StudentWarningsSummaryDetailsParams, 'warningGroupType'>,
    groupType?: StudentWarningsGroupType,
  ) => {
    const response = await request<BlobPart>(
      `maintainers-portal/student-warnings/summary/export-summary`,
      {
        responseType: 'blob',
        params: {
          ...params,
          groupType: groupType,
        },
      },
    );
    if (response.error) {
      throw new Error('Error fetching student data');
    }
    return response.data;
  },
);

const importTemplateAction = actionKitImpFunction(
  async (groupType: StudentWarningsGroupType) => {
    const response = await request<BlobPart>(
      `maintainers-portal/student-warnings/summary/import-template/${groupType}`,
      {
        responseType: 'blob',
      },
    );
    if (response.error) {
      throw new Error('Error fetching template');
    }
    return response.data;
  },
);

const importAction = actionKitImpFunction(
  async (file: File, groupType: StudentWarningsGroupType, schoolId: number) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await request<BlobPart | undefined>(
      `maintainers-portal/student-warnings/summary/import-template/${groupType}/${schoolId}`,
      {
        method: 'post',
        responseType: 'blob',
        data: formData,
      },
    );
    if (response.error) {
      throw new Error('Error fetching template');
    }
    if (response.status === 200) {
      return null;
    }
    return response.data;
  },
);

const createWarningRecord = actionKitImpFunction(
  async (type: StudentWarningsGroupType, data: Record<string, any>) => {
    const response = await request<void>(
      `maintainers-portal/student-warnings/${type}/create`,
      {
        method: 'POST',
        data,
      },
    );
    if (response.error) {
      throw new Error('Error saving student data');
    }
  },
);

const getSessionEnrollmentsActions = actionKitImpFunction(
  async ({
    sessionId,
    page,
    ...params
  }: PaginationQueryDto<StudentRiskEvaluationSessionParams>) => {
    const response = await request<
      PaginationRequestType<StudentRiskEvaluationSessionEnrollment>
    >(
      `maintainers-portal/student-risk-evaluation-session/${sessionId}/enrollments`,
      {
        params: {
          ...params,
          page: Math.max(page - 1, 0),
        },
      },
    );
    if (response.error) {
      throw new Error('Error saving student data');
    }

    return response.data;
  },
);

const getRiskeEvaluationSessionDetails = actionKitImpFunction(
  async ({ sessionId }: { sessionId: string }) => {
    const response = await request<StudentRiskEvaluationSession>(
      `maintainers-portal/student-risk-evaluation-session/${sessionId}/details`,
    );
    if (response.error) {
      throw new Error('Error saving student data');
    }

    return response.data;
  },
);

export {
  getFormDataAction,
  getFormDetailsAction,
  getSummaryPaginatedDataAction,
  getDetailsAction,
  exportDetailsAction,
  exportSummaryAction,
  createWarningRecord,
  importAction,
  importTemplateAction,
  getEvaluationSessionsAction,
  getSessionEnrollmentsActions,
  getRiskeEvaluationSessionDetails,
};
